import { useEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { RTCService, tracker } from 'services';
import { Backstage } from 'containers';
import { Loader } from 'components';
import { CameraOnNewIcon, MicrophoneOnNewIcon } from 'components/Icon';

import { useLiveEvent } from './LiveEvent.context';

import styles from './LiveEvent.JoinPanel.module.scss';

export type JoinPanelProps = {
  isReady: boolean;
  onJoined?(audioMuted: boolean, videoMuted: boolean): Promise<void>;
};

const JoinPanel = ({ isReady = false, onJoined }: JoinPanelProps): JSX.Element => {
  const liveEvent = useLiveEvent();
  const closePreviewTracksOnClose = useRef(true);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(
    () => () => {
      if (closePreviewTracksOnClose.current) {
        RTCService.closeLocalTracks();
      }
    },
    []
  );

  const handleOnJoinWithMicOnly = async () => {
    closePreviewTracksOnClose.current = false;

    setLoading(true);

    if (RTCService.cameraTrack) {
      await RTCService.closeLocalTrack(RTCService.cameraTrack);
      RTCService.cameraTrack = null;
    }

    await onJoined?.(false, true);
    setLoading(false);

    tracker.joinStage(liveEvent.eventCode);
  };

  const handleOnJoin = async () => {
    closePreviewTracksOnClose.current = false;

    setLoading(true);
    await onJoined?.(false, false);
    setLoading(false);

    tracker.joinStage(liveEvent.eventCode);
  };

  const handleOnToggleAudio = (muted: boolean) => tracker.toggleMicrophoneOnJoinPanel(liveEvent.eventCode, !muted);

  const handleOnToggleVideo = (muted: boolean) => tracker.toggleCameraOnJoinPanel(liveEvent.eventCode, !muted);

  return (
    <div className={styles.container}>
      <Backstage onAudioMutedChanged={handleOnToggleAudio} onVideoMutedChanged={handleOnToggleVideo}>
        {loading && <Loader />}

        <div className={styles.preview}>
          <Backstage.Microphone />
          <Backstage.Camera />
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={cx(styles.action, styles.join, styles.micOnly)}
            disabled={!isReady}
            onClick={handleOnJoinWithMicOnly}
          >
            <span>Join with</span>
            <MicrophoneOnNewIcon />
          </button>

          <button type="button" className={cx(styles.action, styles.join)} disabled={!isReady} onClick={handleOnJoin}>
            <span>Join with</span>
            <MicrophoneOnNewIcon />
            <span>+</span>
            <CameraOnNewIcon />
          </button>
        </div>
      </Backstage>
    </div>
  );
};

export default JoinPanel;
